import { Card, Flex, Icon, SimpleGrid, Text, List } from "@chakra-ui/react";
import { Button, Heading, Link } from "@digital-services-gard-as/anchor-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FiTool, FiVideo } from "react-icons/fi";
import { useAuth } from "react-oidc-context";
import { DocumentIcon, PaperPlaneIcon, StarIcon, CheckmarkCircleIcon } from "@digital-services-gard-as/anchor-icons";

const ExternalLandingPage = () => {
    const { signinRedirect } = useAuth();
    const { t } = useTranslation("externalLanding");

    const handleSigninRedirect = useCallback(() => {
        signinRedirect();
    }, [signinRedirect]);

    const resetPasswordVideo =
        // eslint-disable-next-line max-len
        "https://videos.eu.ctfassets.net/jchk06tdml2i/5QoTsPRBENLOluXvvKC3Pc/acaf366cd21616797aa35d067b729e9a/Gard_Claims_invoicing_portal_-_Reset_password.mp4";

    const mfaVideo =
        // eslint-disable-next-line max-len
        "https://videos.eu.ctfassets.net/jchk06tdml2i/5jmrC8tfcpTPPGfx9avDZg/a07d81d64b4245d2978c902c1c1db240/Gard_Claims_invoicing_portal_-_Multi-factor_authentication.mp4";

    const howToDocument =
        // eslint-disable-next-line max-len
        "https://assets.eu.ctfassets.net/jchk06tdml2i/74WRrLTukAPDzMhIlcya1J/cd54c5e9b0ba675e9adc6e2de9cf0235/On_behalf_of_how_to.pdf";

    const ocrHowToDocument =
        // eslint-disable-next-line max-len
        "https://assets.eu.ctfassets.net/jchk06tdml2i/7cojneqVDxqYMXFJ95mZWl/2d52d8cafafbbff487ef38a2de604fd3/User_guide_v2_4.pdf";

    return (
        <Flex flexDir="column" m="7rem" alignItems="center">
            <Flex flexDir="column" gap="2rem" alignItems="center">
                <Heading size="xl">{t("title")}</Heading>
                <Text fontSize="lg">{t("ingress")}</Text>
                <Button id="externalLandingLoginBtn" onClick={handleSigninRedirect} variant="primary">
                    {t("loginBtn")}
                </Button>
            </Flex>
            {/* eslint-disable-next-line no-magic-numbers */}
            <SimpleGrid columns={{ sm: 1, lg: 3 }} gap={8} mt="6rem">
                <Card.Root w="18rem">
                    <Card.Header>
                        <Flex alignItems="center" gap="0.5rem">
                            <Icon boxSize={8} color="anchor.yellow.900" asChild>
                                <StarIcon />
                            </Icon>
                            <Heading size="sm">{t("features.heading")}</Heading>
                        </Flex>
                    </Card.Header>
                    <Card.Body>
                        <List.Root as="ul" pl="1rem">
                            <List.Item _marker={{ color: "inherit" }}>{t("inProgress.ocrScan")}</List.Item>
                            <List.Item _marker={{ color: "inherit" }}>
                                <Text>{t("features.actOnBehalfOf")}</Text>
                                <Link href={howToDocument} target="_blank" rel="noreferrer">
                                    {t("features.seeHowTo")}
                                </Link>
                            </List.Item>
                        </List.Root>
                    </Card.Body>
                </Card.Root>
                <Card.Root w="18rem">
                    <Card.Header>
                        <Flex alignItems="center" gap="0.5rem">
                            <Icon boxSize={8} color="anchor.green.500" asChild>
                                <CheckmarkCircleIcon />
                            </Icon>
                            <Heading size="sm">{t("threeStepProcess.heading")}</Heading>
                        </Flex>
                    </Card.Header>
                    <Card.Body>
                        <Text pb="0.5rem">{t("threeStepProcess.invoice")}</Text>
                        <List.Root as="ol" pl="1rem">
                            <List.Item _marker={{ color: "inherit" }}>{t("threeStepProcess.findRightClaim")}</List.Item>
                            <List.Item _marker={{ color: "inherit" }}>{t("threeStepProcess.generalInfo")}</List.Item>
                            <List.Item _marker={{ color: "inherit" }}>{t("threeStepProcess.totalExpense")}</List.Item>
                        </List.Root>
                    </Card.Body>
                </Card.Root>
                <Card.Root w="18rem">
                    <Card.Header>
                        <Flex alignItems="center" gap="0.5rem">
                            <Icon boxSize={6} color="anchor.navy.500" asChild>
                                <PaperPlaneIcon />
                            </Icon>
                            <Heading size="sm">{t("support.heading")}</Heading>
                        </Flex>
                    </Card.Header>
                    <Card.Body>
                        <Text pb="0.5rem">{t("support.claims")}</Text>
                        <Text pb="0.5rem">{t("support.inNeed")}</Text>
                        <Link href="mailto:claims.invoicing@gard.no">claims.invoicing@gard.no</Link>
                    </Card.Body>
                </Card.Root>
                <Card.Root w="18rem">
                    <Card.Header>
                        <Flex alignItems="center" gap="0.5rem">
                            <Icon boxSize={6} color="anchor.navy.500" asChild>
                                <FiTool />
                            </Icon>
                            <Heading size="sm">{t("inProgress.heading")}</Heading>
                        </Flex>
                    </Card.Header>
                    <Card.Body>
                        <List.Root as="ul" pl="1rem">
                            <List.Item _marker={{ color: "inherit" }}>{t("inProgress.notification")}</List.Item>
                            <List.Item _marker={{ color: "inherit" }}>{t("inProgress.other")}</List.Item>
                        </List.Root>
                    </Card.Body>
                </Card.Root>
                <Card.Root w="18rem">
                    <Card.Header>
                        <Flex alignItems="center" gap="0.5rem">
                            <Icon boxSize={6} color="anchor.navy.500" asChild>
                                <DocumentIcon />
                            </Icon>
                            <Heading size="sm">{t("helpfulDocuments.heading")}</Heading>
                        </Flex>
                    </Card.Header>
                    <Card.Body>
                        <List.Root as="ul" pl="1rem">
                            <List.Item mb="1rem" _marker={{ color: "inherit" }}>
                                <Link href={howToDocument} target="_blank" rel="noreferrer">
                                    {t("helpfulDocuments.seeHowTo")}
                                </Link>
                            </List.Item>
                            <List.Item mb="1rem" _marker={{ color: "inherit" }}>
                                <Link href={ocrHowToDocument} target="_blank" rel="noreferrer">
                                    {t("tutorials.ocrScan")}
                                </Link>
                            </List.Item>
                        </List.Root>
                    </Card.Body>
                </Card.Root>
                <Card.Root w="18rem">
                    <Card.Header>
                        <Flex alignItems="center" gap="0.5rem">
                            <Icon boxSize={6} color="anchor.navy.500" asChild>
                                <FiVideo />
                            </Icon>
                            <Heading size="sm">{t("tutorials.heading")}</Heading>
                        </Flex>
                    </Card.Header>
                    <Card.Body>
                        <List.Root as="ul" pl="1rem">
                            <List.Item mb="1rem" _marker={{ color: "inherit" }}>
                                <Link href={resetPasswordVideo} target="_blank" rel="noreferrer">
                                    {t("tutorials.resetPassword")}
                                </Link>
                            </List.Item>
                            <List.Item _marker={{ color: "inherit" }}>
                                <Link href={mfaVideo} target="_blank" rel="noreferrer">
                                    {t("tutorials.mfa")}
                                </Link>
                            </List.Item>
                        </List.Root>
                    </Card.Body>
                </Card.Root>
            </SimpleGrid>
        </Flex>
    );
};

export default ExternalLandingPage;
